import { Input } from "@/components/ui/input";
import { memo } from "react";
import { getCurrencySymbol } from "@/lib/currency";

interface AllocationInputsProps {
  percentageInputId: string;
  dollarInputId: string;
  percentageInput: string;
  dollarInput: string;
  onPercentageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDollarChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  // mode: 'percentage' for model portfolios, 'dollar' for client/personal
  mode?: 'percentage' | 'dollar';
  // display currency code for symbol rendering
  currencyCode?: string;
}

export const AllocationInputs = memo(({
  percentageInputId,
  dollarInputId,
  percentageInput,
  dollarInput,
  onPercentageChange,
  onDollarChange,
  onBlur,
  mode,
  currencyCode,
}: AllocationInputsProps) => {
  // Determine which field is active/editable based on portfolio type mode
  const effectiveMode: 'percentage' | 'dollar' = mode ?? 'percentage';
  const isPercentageActive = effectiveMode === 'percentage';
  const isDollarActive = effectiveMode === 'dollar';
  const symbol = getCurrencySymbol(currencyCode);

  return (
    <div className="flex flex-wrap gap-4">
      <div className="relative flex-shrink-0">
        <label htmlFor={percentageInputId} className="sr-only">Percentage Allocation</label>
        <Input
          id={percentageInputId}
          type="text"
          inputMode="decimal"
          pattern="^\\d*(\\.\\d{0,2})?$"
          className={`w-32 text-right pr-6 pl-8 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            !isPercentageActive ? 'opacity-50 cursor-not-allowed bg-muted/30' : ''
          }`}
          value={percentageInput}
          onChange={onPercentageChange}
          onBlur={onBlur}
          disabled={!isPercentageActive}
          title={!isPercentageActive ? 'Percentage is derived from dollar amounts for this portfolio type' : 'Edit percentage allocation'}
        />
        <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${
          !isPercentageActive ? 'text-muted-foreground/50' : 'text-muted-foreground'
        }`}>
          %
        </span>
      </div>
      <div className="relative flex-shrink-0">
        <label htmlFor={dollarInputId} className="sr-only">Dollar Amount</label>
        <div className="flex items-center gap-1">
          <Input
            id={dollarInputId}
            type="text"
            inputMode="decimal"
            pattern="^\\d*(\\.\\d{0,2})?$"
            className={`w-40 text-right pr-2 pl-7 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
              !isDollarActive ? 'opacity-50 cursor-not-allowed bg-muted/30' : ''
            }`}
            value={dollarInput}
            onChange={onDollarChange}
            onBlur={onBlur}
            disabled={!isDollarActive}
            title={!isDollarActive ? 'Dollar amount is derived from percentage for this portfolio type' : 'Edit dollar amount'}
          />
        </div>
        <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${
          !isDollarActive ? 'text-muted-foreground/50' : 'text-muted-foreground'
        }`}>
          {symbol}
        </span>
      </div>
    </div>
  );
});

AllocationInputs.displayName = 'AllocationInputs';